import { useTable, usePagination, useSortBy, useExpanded, useGroupBy } from 'react-table';

import React, { useCallback, useEffect, useState } from 'react';
import _, { isEmpty } from 'lodash';
import moment from 'moment';
import { LoadingType, TableParamsType } from '../../../types/common';
import CustomDropdownWithIcon from '../../../components/custom-dropdown/customDropdownWithIcon';
import {
  TableContainer,
  TableSorting,
  TableStyle,
  TableTopContainer,
  TableWrapper,
} from '../../../components/table/table.style';
import Button from '../../../components/button';
import { RadioButtonWrapper } from '../../../components/radio-button/radioButton.styled';
import Heading from '../../../components/typography';
import { SkeletonLine } from '../../../components/skeleton';
import { COLORS } from '../../../constants/style/color';
import PaginationComponent from '../../../components/table/pagination';
import FlashMessage from '../../../components/flash-message';
import { getTableCell } from '../TableSettings';
import EditableCell from './EditableCell';
import clsx from 'clsx';
import useDynamicTable from '../hooks/useDynamicTable';
import SubRow from './SubRow';

export interface ComputeRule {
  sourceFields: string[];
  operator: 'add' | 'subtract' | 'multiply' | 'divide';
  targetField: string;
}

interface ColumnDefinition {
  Header: string;
  accessor: string;
  width?: number;
  editable?: boolean;
  [key: string]: any;
}

export interface TableType {
  columns: ColumnDefinition[];
  subRowColumns?: any[];
  data: any;
  loading?: LoadingType;
  totalCounts: number;
  showPagination?: boolean;
  showPaginationList?: boolean;
  showRow?: boolean;
  pageOfIndex?: number;
  tableInfo: TableParamsType;
  setTableInfo?: any;
  containerClassName?: string;
  checkedValues?: string[];
  showCheckBox?: boolean;
  onChecked?: any;
  border?: string;
  padding?: number;
  checkboxFor?: string;
  coloredTable?: boolean;
  showColumn?: boolean;
  showBulkExport?: boolean;
  hasHeader?: boolean;
  isTableFor?: string;
  scrollableHeight?: number;
  tableColumnList?: any;
  selectedColumnList?: string[];
  handleTableColumn?: (columnValue: string) => void;
  handleExportBulk?: (value?: string) => void;
  handleSendInvite?: (value?: string) => void;

  handleColumnSave?: (value: string[]) => void;
  tablePreferenceLoading?: boolean;
  setIsHovering?: any;
  filteredData?: any;
  selectedTableData?: any;
  setSelectedTableData?: any;
  showSendInvite?: boolean;
  onSave?: (editedData: any[]) => Promise<void>;
  isSaveLoading?: boolean;
  groupBy?: string[];
  subRowKey?: string;
  computeRules?: ComputeRule[];
  selectExport?: any;
  checkedItems?: any;
  setCheckedItems?: any;
  setInviteStatus?: any;
  filteredKey?: string;
}

export type EditedRow = any;

const Table = ({
  columns,
  subRowColumns,
  showPagination = false,
  data,
  loading,
  totalCounts,
  tableInfo,
  setTableInfo,
  containerClassName,
  showPaginationList = true,
  showRow = true,
  border,
  padding,
  checkedValues,
  showCheckBox = false,
  checkboxFor,
  onChecked,
  coloredTable = false,
  isTableFor,
  scrollableHeight,
  showColumn = false,
  showBulkExport = false,
  showSendInvite = false,
  tableColumnList,
  selectedColumnList,
  handleExportBulk,
  handleColumnSave,
  tablePreferenceLoading,
  setIsHovering,
  filteredData,
  selectedTableData,
  setSelectedTableData,
  handleSendInvite,
  onSave,
  isSaveLoading,
  groupBy = [],
  subRowKey = '',
  computeRules = [],
}: TableType): JSX.Element => {
  const { errors, setErrors } = useDynamicTable();
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSizeTotal, setPageSizeTotal] = useState(10);
  const [columnHeader, setColumnHeader] = useState(selectedColumnList);
  const [sortField, setSortField] = useState('');
  const [order, setOrder] = useState('');
  const handleMouseOver = (id: string): void => {
    setIsHovering(id);
  };
  // New states for editable functionality
  const [editingRows, setEditingRows] = useState<Set<string>>(new Set());
  // State to manage table data
  const [dataState, setDataState] = useState<any[]>(data);
  const isGroupingEnabled = Boolean(groupBy.length);

  useEffect(() => {
    setDataState(data);
  }, [data]);

  useEffect(() => {
    if (data) {
      const newData = data.map((row: any, index: number) => {
        const updatedRow = {
          ...row,
          sno: index.toString(),
        };

        if (row[subRowKey]) {
          updatedRow[subRowKey] = row[subRowKey].map((subRow: any, subIndex: number) => ({
            ...subRow,
            sno: `${index}.${subIndex}`,
          }));
        }

        return updatedRow;
      });

      setDataState(newData);
    }
  }, [data, subRowKey]);

  const resetEditedValues = () => {
    setEditingRows(new Set());
    setDataState(data);
  };

  const handleWholeRowEdit = (rowId: string) => {
    setEditingRows((prev) => {
      const newSet = new Set(prev);

      // If it's a parent row
      if (!rowId.includes('.')) {
        const parentIndex = parseInt(rowId);
        newSet.add(rowId);

        // Add all sub rows
        if (dataState[parentIndex]?.[subRowKey]) {
          dataState[parentIndex][subRowKey].forEach((_: any, subIndex: number) => {
            newSet.add(`${parentIndex}.${subIndex}`);
          });
        }
      }
      // If it's a sub row
      else {
        const [parentIndex] = rowId.split('.');
        newSet.add(rowId); // Add sub row
        newSet.add(parentIndex); // Add parent row

        // Add siblings
        if (dataState[parseInt(parentIndex)]?.[subRowKey]) {
          dataState[parseInt(parentIndex)][subRowKey].forEach((_: any, subIndex: number) => {
            newSet.add(`${parentIndex}.${subIndex}`);
          });
        }
      }

      return newSet;
    });

    // Focus first editable cell
    const cell = columns.find((column: any) => column?.editable);
    if (cell) {
      setTimeout(
        () =>
          requestAnimationFrame(() => {
            const target = document.getElementById(`${cell?.accessor}-${rowId}`);
            target?.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'center',
            });
          }),
        0
      );
    }
  };

  const handleRowEdit = (rowId: string) => {
    handleWholeRowEdit(rowId); // Reuse same logic
  };

  const handleCancel = (index?: number) => {
    if (index !== null && index !== undefined && typeof index === 'number') {
      setEditingRows((prev) => {
        const newSet = new Set(prev);
        // Remove parent row
        newSet.delete(index?.toString());

        // Remove all sub rows of this parent
        if (dataState[index]?.[subRowKey]) {
          dataState[index][subRowKey].forEach((_: any, subIndex: number) => {
            newSet.delete(`${index}.${subIndex}`);
          });
        }
        return newSet;
      });

      setDataState((prevData) =>
        prevData.map((row, i) => {
          if (index === i) {
            // Deep copy the original row and its sub rows
            const originalRow = { ...data[i] };
            if (data[i]?.[subRowKey]) {
              originalRow[subRowKey] = data[i][subRowKey].map((subRow: any) => ({
                ...subRow,
              }));
            }
            return originalRow;
          }
          return row;
        })
      );
      const err = errors.filter((error) => error.sNo != index.toString());
      setErrors(err);
    } else {
      setEditingRows(new Set());
      setDataState(data);
      setErrors([]);
    }
  };

  const handleEdit = (
    rowIndex: number | string,
    columnId: string,
    value: any,
    defaultValueName?: string
  ) => {
    setDataState((prevData) => {
      const calculateComputedValues = (row: any) => {
        const updates: any = {};

        computeRules?.forEach((rule) => {
          if (rule.sourceFields.includes(columnId)) {
            const values = rule.sourceFields.map((field) => {
              return field === columnId ? Number(value) : Number(row[field]);
            });

            let result;
            switch (rule.operator) {
              case 'add':
                result = values.reduce((a, b) => a + b, 0);
                break;
              case 'subtract':
                result = values.reduce((a, b) => a - b);
                break;
              case 'multiply':
                result = values.reduce((a, b) => a * b, 1);
                break;
              case 'divide':
                result = values.reduce((a, b) => a / b);
                break;
            }

            updates[rule.targetField] = result === 0 ? null : result;
          }
        });

        return updates;
      };

      // Handle sub row edit
      if (typeof rowIndex === 'string' && rowIndex.includes('.')) {
        const [parentIndex, subIndex] = rowIndex.split('.').map(Number);

        return prevData.map((row, index) => {
          if (index === parentIndex) {
            const updatedSubRows = row[subRowKey].map((subRow: any, sIndex: number) => {
              if (sIndex === subIndex) {
                const computedValues = calculateComputedValues(subRow);
                return {
                  ...subRow,
                  [defaultValueName ?? columnId]: value,
                  ...computedValues,
                  sno: rowIndex,
                };
              }
              return subRow;
            });

            return {
              ...row,
              [subRowKey]: updatedSubRows,
              sno: parentIndex.toString(),
            };
          }
          return row;
        });
      }

      // Handle parent row edit
      return prevData.map((row, index) => {
        if (index === Number(rowIndex)) {
          const computedValues = calculateComputedValues(row);
          return {
            ...row,
            [defaultValueName ?? columnId]: value,
            ...computedValues,
            sno: rowIndex.toString(),
          };
        }
        return row;
      });
    });
  };

  const handleSave = async (index?: number) => {
    const isSingleAction = index !== undefined && typeof index === 'number';
    let editedData = [];

    if (isSingleAction) {
      if (editingRows.has(index.toString())) {
        const parentRow = dataState[index];
        const editedRow = { ...parentRow };

        // Include sub rows if they exist
        if (parentRow[subRowKey]) {
          editedRow[subRowKey] = parentRow[subRowKey].map((subRow: any) => ({ ...subRow }));
        }

        editedData = [editedRow];
      }
    } else {
      // Save all edited rows
      editedData = dataState.filter((row, idx) => editingRows.has(idx.toString()));
    }

    if (onSave && editedData.length > 0) {
      await onSave(editedData);

      if (isSingleAction) {
        // Clear single row edit state
        editingRows.delete(index.toString());
        // Clear sub row edit states
        if (dataState[index]?.[subRowKey]) {
          dataState[index][subRowKey].forEach((_: any, subIndex: number) => {
            editingRows.delete(`${index}.${subIndex}`);
          });
        }
        const err = errors.filter((error) => error.sNo && !error.sNo.startsWith(index.toString()));
        setErrors(err);
      } else {
        // Clear all edit states
        editingRows.clear();
        setErrors([]);
      }

      setEditingRows(new Set(editingRows));
    }
  };

  // Update hasChanges and hasChangesAtIndex to check sub rows
  const hasChanges = () => {
    return dataState.some((row, index) => {
      const baseChange = !_.isEqual(row, data[index]);
      const subRowChanges = row[subRowKey]?.some(
        (subRow: any, subIndex: number) => !_.isEqual(subRow, data[index][subRowKey][subIndex])
      );
      return baseChange || subRowChanges;
    });
  };

  const hasChangesAtIndex = (index: number) => {
    const baseChange = !_.isEqual(dataState[index], data[index]);
    const subRowChanges = dataState[index]?.[subRowKey]?.some(
      (subRow: any, subIndex: number) => !_.isEqual(subRow, data[index][subRowKey][subIndex])
    );
    return baseChange || subRowChanges;
  };

  useEffect(() => {
    if (tableInfo?.sorting === '') {
      setOrder('');
    }
  }, [tableInfo?.sorting]);

  const handleMouseOut = (): void => {
    setIsHovering('');
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    setPageSize,
    state,
    toggleAllRowsExpanded,
  } = useTable(
    {
      columns,
      // data,
      data: dataState,
      initialState: { pageSize: totalCounts, groupBy },
    },
    useGroupBy,
    useSortBy,
    useExpanded,
    usePagination
  );
  // console.log('headerGroups', headerGroups);

  useEffect(() => {
    setColumnHeader(selectedColumnList);
  }, [selectedColumnList]);

  // Fixing rows count when groupBy is enabled
  const totalGroupByKeys = React.useMemo(() => {
    const keys = new Set();
    dataState.forEach((row) => {
      row[groupBy[0]] && keys.add(row[groupBy[0]]);
    });
    return keys.size;
  }, [page]);

  const getFlattenedRows = (rows: any[]) => {
    let flatRows: any[] = [];

    const flatten = (rows: any[]) => {
      rows.forEach((row) => {
        flatRows.push(row);
        if (row.isExpanded && row.subRows) {
          flatten(row.subRows);
        }
      });
    };

    flatten(rows);
    return flatRows;
  };
  const flattenedRows = React.useMemo(() => getFlattenedRows(page), [page]);

  const finalCounts = isGroupingEnabled ? totalCounts + flattenedRows.length : totalCounts;
  // Ends here

  useEffect(() => {
    if (tableInfo.skipCount === 0) {
      setPageIndex(1);
    }
    if (tableInfo.maxResultCount !== 0 && tableInfo.maxResultCount != null) {
      setPageSizeTotal(tableInfo.maxResultCount);
    }
    resetEditedValues();
  }, [tableInfo]);

  useEffect(() => {
    if (!showPagination || !showPaginationList) {
      // if any bugs occurs in pagination, check this line. May be replace it with totalCounts
      setPageSize(finalCounts);
      resetEditedValues();
    }
  }, [showPagination, finalCounts, showPaginationList, isTableFor]);

  const capitalizeFirst = (str: string): string => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const handleColumn = (columnValue: string): void => {
    let filterList = columnHeader != null ? [...columnHeader] : [];
    if (filterList.includes(columnValue)) {
      filterList = filterList.filter((item) => item !== columnValue);
    } else {
      filterList.push(columnValue);
    }
    setColumnHeader(filterList);
  };

  const handleSortingChange = (accessor: any, sortable: boolean = true): void => {
    const sortingTitle = capitalizeFirst(accessor);
    const sortOrder =
      accessor === sortField && order === 'Asc'
        ? 'Desc'
        : accessor === sortField && order === 'Desc'
          ? ''
          : 'Asc';
    setSortField(accessor);

    sortable &&
      setTableInfo({
        ...tableInfo,
        sorting: isEmpty(sortOrder) ? '' : `${sortingTitle} ${sortOrder}`,
      });
    sortable && resetEditedValues();
    setOrder(sortOrder);
  };

  // Checkbox function
  const [checkedKeys, setCheckedKeys] = useState<Array<string | number>>([]);
  let checked = false;

  useEffect(() => {
    if (!isEmpty(checkedValues) && isEmpty(checkedKeys)) {
      setCheckedKeys(checkedValues ?? []);
    }
    if (checkedValues !== checkedKeys) {
      setCheckedKeys(checkedValues ?? []);
    }
    if (isEmpty(checkedValues)) {
      setCheckedKeys([]);
    }
  }, [checkedValues]);

  useEffect(() => {
    if (loading === 'success' && data.length === 0 && tableInfo.skipCount > 0) {
      setPageIndex(tableInfo.skipCount / 10);
      setTableInfo({ ...tableInfo, skipCount: tableInfo.skipCount - 10 });
    }
  }, [data, loading]);

  const PendingData = data?.filter((item: any) => item.status === 'Pending');
  const ApprovedData = data?.filter((item: any) => item.status === 'Approved');
  const DownloadableData = data?.filter((item: any) => item.isDownloadable);
  if (checkedKeys.length === data?.length && checkedKeys.length !== 0) {
    checked = true;
  }
  if (filteredData && checkedKeys.length === filteredData?.length && checkedKeys.length !== 0) {
    checked = true;
  }

  if (
    checkboxFor === 'Pending' &&
    checkedKeys.length === PendingData?.length &&
    checkedKeys.length !== 0
  ) {
    checked = true;
  }
  if (
    checkboxFor === 'Approved' &&
    checkedKeys.length === ApprovedData?.length &&
    checkedKeys.length !== 0
  ) {
    checked = true;
  }
  if (
    checkboxFor === 'Downloadable' &&
    checkedKeys.length === DownloadableData?.length &&
    checkedKeys.length !== 0
  ) {
    checked = true;
  } else if (checkedKeys.length === 0) {
    checked = false;
  }

  const handleCheckAll = (checked: boolean): void => {
    const keys = checked
      ? !isEmpty(checkedKeys)
        ? []
        : filteredData !== undefined
          ? filteredData.map((items: any) => items.id)
          : checkboxFor === 'Pending'
            ? PendingData.map((items: any) => items.id)
            : checkboxFor === 'Approved' &&
              (isTableFor === 'RsuAwards' || isTableFor === 'OptionAwards')
              ? ApprovedData.filter((data: any) => data?.isEmailNotificationSent === false).map(
                (e: any) => e.id
              )
              : isTableFor === 'Release'
                ? data.map((items: any) => items.id)
                : isTableFor === 'Exercise'
                  ? data.map((items: any) => items.id)
                  : isTableFor === 'TransferAndAllocate'
                    ? data.map((items: any) => items.id)
                    : checkboxFor === 'Approved'
                      ? ApprovedData.map((items: any) => items.id) != null
                        ? data.map((item: any) => item.id)
                        : []
                      : checkboxFor === 'Downloadable'
                        ? DownloadableData.map((items: any) => items.id)
                        : data.map((items: any) => items.id)
      : [];

    const value =
      checked &&
        (isTableFor === 'Release' ||
          isTableFor === 'TransferAndAllocate' ||
          isTableFor === 'Exercise')
        ? data.map((items: any) => items)
        : [];
    setCheckedKeys(keys);
    setSelectedTableData && setSelectedTableData(value);
    showCheckBox && Boolean(onChecked) && onChecked(keys);
  };

  const handleCheck = (value: string, checked: boolean): void => {
    const keys: any = checked
      ? [...checkedKeys, value]
      : checkedKeys.filter((item) => item !== value);

    setCheckedKeys(keys);
    showCheckBox && Boolean(onChecked) && onChecked(keys);
  };
  const handleCheckedValue = (value: string, checked: boolean, selectedValue: any): void => {
    const keys: any = checked
      ? [...checkedKeys, value]
      : checkedKeys.filter((item) => item !== value);

    const newSelectedValue: any =
      selectedTableData && checked
        ? [...selectedTableData, selectedValue]
        : selectedTableData?.filter((item: any) => item?.id !== selectedValue?.id);
    setCheckedKeys(keys);
    if (isTableFor === 'Release') {
      setSelectedTableData(
        newSelectedValue.map((data: any) => {
          return { ...data, rsuVestingId: data?.id };
        })
      );
    }
    if (isTableFor === 'Exercise') {
      setSelectedTableData(
        newSelectedValue.map((data: any) => {
          return { ...data, rsuVestingId: data?.id };
        })
      );
    }
    if (isTableFor === 'TransferAndAllocate') {
      setSelectedTableData(
        newSelectedValue.map((data: any) => {
          return { ...data };
        })
      );
    }
    showCheckBox && Boolean(onChecked) && onChecked(keys);
  };
  const checkTableBodyHeight =
    loading === 'loading'
      ? 'auto'
      : data.length === 0
        ? // ? '213px'
        '250px'
        : isGroupingEnabled && totalGroupByKeys === 1
          ? `${data.length * 87 + 81 + (isGroupingEnabled ? 260 : 0)}px`
          : isGroupingEnabled && totalGroupByKeys === 2
            ? `${data.length * 87 + 81 + (isGroupingEnabled ? 80 : 0)}px`
            : subRowColumns && subRowKey
              ? `${(data.length + data?.[subRowKey]?.length) * 87 + 81}px`
              : Boolean(data) && data.length < 6
                ? `${data.length * 87 + 81}px`
                : `${scrollableHeight ?? 500}px`;

  // const showSaveBtn = editingRows.size > 1;

  // Add this function to count unique parent rows being edited
  const getUniqueParentRowsCount = () => {
    const uniqueParents = new Set();

    editingRows.forEach((rowId) => {
      // If it's a sub row (contains '-'), get parent index
      if (rowId.includes('.')) {
        const parentIndex = rowId.split('.')[0];
        uniqueParents.add(parentIndex);
      } else {
        // It's already a parent row
        uniqueParents.add(rowId);
      }
    });
    return uniqueParents.size;
  };

  // Replace the showSaveBtn check with:
  const showBulkSaveButtons = getUniqueParentRowsCount() > 1;

  // const hasChanges = () => {
  //   return dataState.some((row, index) => !_.isEqual(row, data[index]));
  // };
  // const hasChangesAtIndex = (index: number) => {
  //   return !_.isEqual(dataState[index], data[index]);
  // };
  const hasError = errors.length > 0;
  const errorIndexes = errors.map((error) => error.sNo);
  // console.log("columns", columns);

  return (
    <TableWrapper border={border}>
      {(showColumn || showBulkExport) && (
        <TableTopContainer>
          {showColumn && (
            <CustomDropdownWithIcon
              data={tableColumnList ?? []}
              handleUser={(columnValue) => {
                handleColumn(columnValue);
              }}
              checkedList={columnHeader ?? []}
              handleSubmit={() => {
                handleColumnSave != null && columnHeader != null && handleColumnSave(columnHeader);
              }}
              loading={tablePreferenceLoading}
              width='300px'
              padding='0px 0px 10px 0px'
            />
          )}
          {/* {showBulkExport && DownloadableData.length > 0 && (
              <Button
                id='dropdownId'
                title='Bulk Export XLS'
                type='button'
                variant='secondary-gray'
                onClick={() => {
                  handleExportBulk?.();
                }}
              />
            )} */}
          {showBulkExport && checkedValues !== undefined && checkedValues?.length > 0 && (
            <Button
              id='dropdownId'
              title='Bulk Export XLS'
              type='button'
              variant='secondary-gray'
              onClick={() => {
                handleExportBulk?.();
              }}
            />
          )}
          {showSendInvite && checkedValues !== undefined && checkedValues?.length > 0 && (
            <Button
              id='dropdownId'
              title='Send Invite'
              type='button'
              variant='secondary'
              onClick={() => {
                handleSendInvite?.();
              }}
            />
          )}
        </TableTopContainer>
      )}

      {showBulkSaveButtons && (
        <TableTopContainer className='flex'>
          <Button
            id='saveOnEditBtn'
            title='Save'
            type='button'
            variant='secondary'
            icon='save'
            onClick={() => handleSave()}
            loading={isSaveLoading}
            disabled={!hasChanges()}
          />
          <Button
            id='cancelOnEditBtn'
            title='Cancel'
            type='button'
            variant='secondary-gray'
            icon='close'
            onClick={handleCancel}
            loading={isSaveLoading}
          />
        </TableTopContainer>
      )}

      {isGroupingEnabled && (
        <TableTopContainer>
          <Button
            id='toggleExpandCollapseBtn'
            title={Object.keys(state.expanded).length > 0 ? 'Collapse All' : 'Expand All'}
            type='button'
            variant='secondary-gray'
            icon={Object.keys(state.expanded).length > 0 ? 'expand_less' : 'expand_more'}
            onClick={() => {
              toggleAllRowsExpanded(Object.keys(state.expanded).length === 0);
            }}
          />
        </TableTopContainer>
      )}

      <TableContainer style={{ overflowX: 'auto' }} padding={padding} height={checkTableBodyHeight}>
        <TableStyle
          {...getTableProps}
          data-table-v2
          className={clsx(containerClassName, showBulkSaveButtons && 'hide-action-menu')}>
          <thead>
            {
              // Loop over the header rows
              headerGroups.map((headerGroup: any) => {
                return (
                  // Apply the header row props
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {
                      // Loop over the headers in each row
                      <>
                        {showCheckBox && (
                          <th style={{ minWidth: '10px', width: '10px' }}>
                            <RadioButtonWrapper
                              label='label'
                              className={`${(filteredData !== undefined &&
                                  checkedKeys.length < filteredData?.length &&
                                  checkedKeys.length > 0) ||
                                  (checkboxFor === 'Approved' &&
                                    checkedKeys.length < ApprovedData?.length &&
                                    checkedKeys.length > 0) ||
                                  (checkboxFor === 'Pending' &&
                                    checkedKeys.length < PendingData?.length &&
                                    checkedKeys.length > 0) ||
                                  (checkboxFor === 'Downloadable' &&
                                    checkedKeys.length < DownloadableData?.length &&
                                    checkedKeys.length > 0) ||
                                  (isTableFor === 'Participant' &&
                                    checkedKeys.length < data?.length &&
                                    checkedKeys.length > 0) ||
                                  (checkedKeys?.length < data?.length && checkedKeys?.length > 0)
                                  ? 'checkboxWrapper block partialSelect'
                                  : 'checkboxWrapper block'
                                }  `}>
                              <input
                                type='checkbox'
                                name={'row.index'}
                                checked={checked}
                                onChange={(event: any) => {
                                  handleCheckAll(event.target.checked);
                                }}
                              />
                            </RadioButtonWrapper>
                          </th>
                        )}
                        {isGroupingEnabled && <th style={{ minWidth: '10px', width: '10px' }}></th>}
                        {headerGroup.headers.map(
                          (column: {
                            id: string;
                            sortable?: boolean | undefined;
                            getHeaderProps: any;
                            manualSorting: boolean;
                            getSortByToggleProps: any;
                            width: number;
                            center?: boolean;
                            isSorted: boolean;
                            isSortedDesc: boolean | undefined;
                            Header: string;
                          }) => {
                            return (
                              <th
                                key={column.id}
                                onClick={() => {
                                  data?.length > 0
                                    ? handleSortingChange(column.id, column?.sortable)
                                    : null;
                                }}
                                style={{
                                  width: `${column.width}px`,
                                }}
                                {...column.getHeaderProps(
                                  Boolean(column?.manualSorting) && column.getSortByToggleProps(),
                                  {
                                    style: {
                                      width: column.width,
                                    },
                                  }
                                )}>
                                <TableSorting
                                  className={`flex items-start custom-header--main justify-${column.center ?? false ? 'center' : 'start'
                                    }`}>
                                  <Heading variant='body-s' title={column.Header} />
                                  {column.sortable !== false && (
                                    <div className='flex flex-col sorting-icon'>
                                      <span
                                        className={`material-symbols-rounded 
                                        ${(column.id === sortField && order === 'Asc') ||
                                            (column.isSorted && !(column.isSortedDesc ?? false))
                                            ? 'active'
                                            : ''
                                          }`}>
                                        arrow_drop_up
                                      </span>
                                      <span
                                        className={`material-symbols-rounded 
                                        ${(column.id === sortField && order === 'Desc') ||
                                            (column.isSortedDesc ?? false)
                                            ? 'active'
                                            : ''
                                          }`}>
                                        arrow_drop_down
                                      </span>
                                    </div>
                                  )}
                                </TableSorting>
                              </th>
                            );
                          }
                        )}
                      </>
                    }
                  </tr>
                );
              })
            }
          </thead>
          <tbody {...getTableBodyProps()}>
            {loading === 'loading' && (
              <>
                {Array.from(Array(6).keys()).map((item) => (
                  <>
                    {headerGroups.map((headerGroup: any) => {
                      return (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                          {
                            <>
                              {showCheckBox && (
                                <td>
                                  <SkeletonLine size='table' width={'20px'} />
                                </td>
                              )}
                              {headerGroup.headers.map((column: any) => {
                                return (
                                  <td
                                    style={{
                                      textAlign: column?.center && 'center',
                                    }}>
                                    <SkeletonLine
                                      size='table'
                                      width={`${String(column.width ?? '')}px`}
                                    />
                                  </td>
                                );
                              })}
                            </>
                          }
                        </tr>
                      );
                    })}
                  </>
                ))}
              </>
            )}
            {loading === 'success' &&
              page.map((row: any, i: number) => {
                prepareRow(row);
                return (
                  <>
                    <tr
                      key={`table-dynamic-row-${i}`}
                      className={clsx(errorIndexes.includes(row?.index?.toString()) && 'error-row')}
                      style={{
                        background: coloredTable
                          ? row?.original?.isExpired === true
                            ? `${COLORS.CulturedWhite}`
                            : new Date() >= moment(row?.original?.vestingDate).toDate()
                              ? `#F0F2FF`
                              : `#E5EFE8`
                          : row?.isGrouped && row?.isExpanded && `${COLORS.CulturedWhite}`,
                        transition: 'background-color 0.5s ease',
                      }}
                      {...row.getRowProps()}
                      onMouseOver={() => {
                        setIsHovering != null && handleMouseOver(row?.id);
                      }}
                      onMouseOut={() => {
                        setIsHovering != null && handleMouseOut();
                      }}
                      onDoubleClick={() => {
                        const isRowEditable = row.cells.find((cell: any) => cell.column.editable);
                        if (isRowEditable) {
                          handleWholeRowEdit(i.toString());
                        }
                      }}>
                      {showCheckBox && (
                        <>
                          <td style={{ minWidth: '10px', width: '10px', paddingLeft: '3px' }}>
                            {filteredData !== undefined ? (
                              filteredData
                                ?.map((item: any) => item?.id)
                                ?.includes(row.original.id) ? (
                                <RadioButtonWrapper label='label' className='block checkboxWrapper'>
                                  <input
                                    type='checkbox'
                                    value={row.original.id}
                                    checked={checkedKeys.some(
                                      (item: any) => item === row.original.id
                                    )}
                                    onChange={(event: any) => {
                                      // debugger;
                                      handleCheck(row.original.id, event.target.checked);
                                    }}
                                  />
                                </RadioButtonWrapper>
                              ) : null
                            ) : checkboxFor === 'Pending' ? (
                              row.original.status === 'Pending' ? (
                                <RadioButtonWrapper label='label' className='block checkboxWrapper'>
                                  <input
                                    type='checkbox'
                                    value={row.original.id}
                                    checked={checkedKeys.some(
                                      (item: any) => item === row.original.id
                                    )}
                                    onChange={(event: any) => {
                                      handleCheck(row.original.id, event.target.checked);
                                    }}
                                  />
                                </RadioButtonWrapper>
                              ) : null
                            ) : checkboxFor === 'Downloadable' ? (
                              row.original.isDownloadable != null &&
                                row.original.isDownloadable !== false ? (
                                <RadioButtonWrapper label='label' className='block checkboxWrapper'>
                                  <input
                                    type='checkbox'
                                    value={row.original.id}
                                    checked={checkedKeys.some(
                                      (item: any) => item === row.original.id
                                    )}
                                    onChange={(event: any) => {
                                      handleCheck(row.original.id, event.target.checked);
                                    }}
                                  />
                                </RadioButtonWrapper>
                              ) : null
                            ) : checkboxFor === 'Approved' ? (
                              row.original.status === 'Approved' ? (
                                row.original.isEmailNotificationSent !== false ? (
                                  row.original.isEmailNotificationSent === false && (
                                    <RadioButtonWrapper
                                      label='label'
                                      className='block checkboxWrapper'>
                                      <input
                                        type='checkbox'
                                        value={row.original.id}
                                        checked={checkedKeys.some(
                                          (item: any) => item === row.original.id
                                        )}
                                        onChange={(event: any) => {
                                          handleCheck(row.original.id, event.target.checked);
                                        }}
                                      />
                                    </RadioButtonWrapper>
                                  )
                                ) : (
                                  <RadioButtonWrapper
                                    label='label'
                                    className='block checkboxWrapper'>
                                    <input
                                      type='checkbox'
                                      value={row.original.id}
                                      checked={checkedKeys.some(
                                        (item: any) => item === row.original.id
                                      )}
                                      onChange={(event: any) => {
                                        handleCheck(row.original.id, event.target.checked);
                                      }}
                                    />
                                  </RadioButtonWrapper>
                                )
                              ) : null
                            ) : isTableFor === 'Exercise' ? (
                              row.original.recordTypeSystemName === 'OptionsExercise' ? (
                                <RadioButtonWrapper label='label' className='block checkboxWrapper'>
                                  <input
                                    type='checkbox'
                                    value={row.original.id}
                                    checked={checkedKeys.some(
                                      (item: any) => item === row.original.id
                                    )}
                                    onChange={(event: any) => {
                                      handleCheckedValue(
                                        row.original.id,
                                        event.target.checked,
                                        row.original
                                      );
                                    }}
                                  />
                                </RadioButtonWrapper>
                              ) : null
                            ) : isTableFor === 'Release' || 'TransferAndAllocate' ? (
                              <RadioButtonWrapper label='label' className='block checkboxWrapper'>
                                <input
                                  type='checkbox'
                                  value={row.original.id}
                                  checked={checkedKeys.some(
                                    (item: any) => item === row.original.id
                                  )}
                                  onChange={(event: any) => {
                                    handleCheckedValue(
                                      row.original.id,
                                      event.target.checked,
                                      row.original
                                    );
                                  }}
                                />
                              </RadioButtonWrapper>
                            ) : (
                              <RadioButtonWrapper label='label' className='block checkboxWrapper'>
                                <input
                                  type='checkbox'
                                  value={row.original.id}
                                  checked={checkedKeys.some(
                                    (item: any) => item === row.original.id
                                  )}
                                  onChange={(event: any) => {
                                    handleCheck(row.original.id, event.target.checked);
                                  }}
                                />
                              </RadioButtonWrapper>
                            )}
                          </td>
                        </>
                      )}
                      {isGroupingEnabled &&
                        (row.isGrouped ? (
                          <td
                            {...row.getToggleRowExpandedProps()}
                            style={{
                              paddingLeft: '10px',
                            }}
                            title='Click to expand/collapse row'>
                            <Button
                              id='expandRowBtn'
                              title=''
                              type='button'
                              variant='small-icon-dark'
                              icon={row.isExpanded ? 'keyboard_arrow_down' : 'chevron_right'}
                            />
                          </td>
                        ) : (
                          <td></td>
                        ))}
                      {row.cells.map((cell: any, index: number) => {
                        const renderActionButtons = (cell: any, row: any) => (
                          <td
                            id={`${cell?.column?.id}-${cell?.row?.index}`}
                            {...cell.getCellProps({
                              style: {
                                minWidth: cell.column.minWidth,
                                width: cell.column.width,
                              },
                            })}>
                            <span className='flex space-x-2'>
                              <Button
                                id='saveOnEditBtn'
                                title=''
                                type='button'
                                variant='rounded-icon-secondary'
                                icon='save'
                                onClick={() => handleSave(row.index)}
                                loading={isSaveLoading}
                                disabled={!hasChangesAtIndex(row.index?.toString())}
                              />
                              <Button
                                id='cancelOnEditBtn'
                                title=''
                                type='button'
                                variant='rounded-icon-secondary-dark'
                                icon='close'
                                onClick={() => handleCancel(row.index)}
                                loading={isSaveLoading}
                              />
                            </span>
                          </td>
                        );

                        const renderCellContent = (cell: any, row: any) => {
                          if (cell.column?.id !== 'actions') {
                            if (editingRows.has(row.index?.toString())) {
                              return null;
                            }
                            return cell.render('Cell', { handleRowEdit });
                          }

                          if (editingRows.has(row.index?.toString()) && editingRows.size === 1) {
                            return renderActionButtons(cell, row);
                          }

                          if (editingRows.has(row.index?.toString())) {
                            return renderActionButtons(cell, row);
                          }

                          return cell.render('Cell', { handleRowEdit });
                        };

                        return (
                          <React.Fragment key={`table-dynamic-cell-${index}`}>
                            {cell.isGrouped ? (
                              <>
                                <td
                                  style={{
                                    minWidth: cell.column.minWidth,
                                    width: cell.column.width,
                                    textAlign: cell.column?.cellCenter && 'center',
                                  }}>
                                  <span>{cell.render('Cell')}</span>
                                </td>
                              </>
                            ) : cell.isAggregated && !cell.column?.renderCell ? (
                              <td
                                style={{
                                  minWidth: cell.column.minWidth,
                                  width: cell.column.width,
                                  textAlign: cell.column?.cellCenter && 'center',
                                }}>
                                {cell.render('Aggregated')}
                              </td>
                            ) : cell.column?.renderCell &&
                              cell.column?.hideOnSubRows &&
                              !cell.isAggregated ? (
                              <td
                                style={{
                                  minWidth: cell.column.minWidth,
                                  width: cell.column.width,
                                }}
                              />
                            ) : cell.column?.renderCell ? (
                              renderCellContent(cell, row)
                            ) : cell.column.editable ? (
                              <EditableCell
                                key={`table-cell-${index}-editable`}
                                cell={cell}
                                value={cell.value}
                                row={row}
                                column={cell.column}
                                isEditable={cell.column.editable}
                                isRowEditable={editingRows.has(row.index?.toString()) ?? false}
                                onRowEdit={handleRowEdit}
                                onEdit={handleEdit}
                                errors={errors}
                              />
                            ) : groupBy?.includes(cell.column.id) ? (
                              <td
                                style={{
                                  minWidth: cell.column.minWidth,
                                  width: cell.column.width,
                                  textAlign: cell.column?.cellCenter && 'center',
                                }}></td>
                            ) : (
                              getTableCell(cell)
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tr>
                    {subRowColumns && subRowKey && (
                      <>
                        <tr className='subrow-header'>
                          <th className='indent-cell'></th>
                          {subRowColumns.map((column, index) => (
                            <th
                              key={`subheader-${column.accessor}-${index}`}
                              style={{
                                width: column.width,
                                textAlign: column.center ? 'center' : 'left',
                              }}
                              className='subrow-header-cell'>
                              {column.Header}
                            </th>
                          ))}
                        </tr>
                        {row.original[subRowKey]?.map((subRow: any, index: number) => {
                          return (
                            <SubRow
                              key={`${subRow.id}-allocation-${index}`}
                              parentId={row.id}
                              parentRow={row}
                              row={subRow}
                              index={index}
                              columns={subRowColumns}
                              editingRows={editingRows}
                              onEdit={(...rest) => {
                                handleEdit(...rest);
                              }}
                              onRowEdit={handleRowEdit}
                              errors={errors as any}
                            />
                          );
                        })}
                      </>
                    )}
                  </>
                );
              })}
            {loading === 'success' && Boolean(page) && page.length === 0 && (
              <tr className='border-0'>
                <td colSpan={100} className='border-b-0'>
                  <div className='flex justify-center'>
                    <FlashMessage size='md' message='No Data Found' type='info' />
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </TableStyle>
      </TableContainer>

      {showPagination && data.length > 0 && (
        <>
          <PaginationComponent
            data={data}
            totalCount={totalCounts}
            pageIndex={pageIndex}
            showRow={showRow}
            pageSize={state.pageSize}
            showPaginationList={showPaginationList}
            maxResultCount={tableInfo.maxResultCount}
            pageSizeTotal={pageSizeTotal}
            handlePaginationInfo={(value: number) => {
              setTableInfo({
                ...tableInfo,
                skipCount: (value - 1) * tableInfo.maxResultCount,
              });
              setPageIndex(value);
            }}
            handleSelectInfo={(value: string) => {
              setTableInfo({
                ...tableInfo,
                skipCount: 0,
                maxResultCount: Number(value),
              });
              setPageSizeTotal(Number(value));
              setPageSize(Number(value));
            }}
          />
        </>
      )}
    </TableWrapper>
  );
};

export default Table;
